import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Link2, Copy, Check, ExternalLink, Mail, Globe, Lock,
  Loader2, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin;

const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const CollectionShareDialog = ({ open, onOpenChange, collection, onCollectionUpdated }) => {
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const isPrivatee = collection?.visibility === 'private';
  const shareId = collection?.share_id;
  const shareUrl = shareId ? `${BASE_URL}/collection/${shareId}` : '';

  const generateShareLink = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(`${API}/collections/${collection.id}/share`);
      onCollectionUpdated({
        ...collection,
        share_id: res.data.share_id,
        visibility: collection.visibility === 'private' ? 'link' : collection.visibility,
      });
    } catch {
      setError('Could not create share link. Please try again.');
    }
    setGenerating(false);
  };

  const deactivateLink = async () => {
    try {
      await axios.delete(`${API}/collections/${collection.id}/share`);
      onCollectionUpdated({ ...collection, share_id: null, visibility: 'private' });
    } catch {
      setError('Could not deaktivere delink.');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const el = document.createElement('input');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sharePinterest = () => {
    const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&description=${encodeURIComponent(`${collection.name} — Curated collection from Create by Lumera`)}`;
    window.open(url, '_blank', 'width=600,height=500');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(`${collection.name} — Create by Lumera`);
    const body = encodeURIComponent(`Hi!\n\nSjekk ut denne the collection: ${collection.name}\n${collection.description ? collection.description + '\n' : ''}\n${shareUrl}\n\nMade with Create by Lumera`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!generating) { onOpenChange(o); setCopied(false); setError(''); } }}>
      <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden bg-white border-stone-100 max-h-[90vh] flex flex-col" data-testid="collection-share-dialog">
        <div className="bg-stone-900 px-6 py-5">
          <DialogHeader>
            <DialogTitle className="text-white font-serif text-xl">Share collection</DialogTitle>
            <p className="text-stone-400 font-sans text-sm mt-1 truncate">{collection?.name}</p>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-5">
          {/* Privatee — needs to generate a link first */}
          {isPrivatee && !shareId ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-stone-400" />
              </div>
              <p className="font-sans font-semibold text-stone-800 text-sm mb-1">Collectionen er privat</p>
              <p className="font-sans text-stone-500 text-xs mb-5">Create a share link so others can see your collection.</p>
              <Button
                data-testid="generate-share-link-btn"
                onClick={generateShareLink}
                disabled={generating}
                className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-6 h-10 text-xs font-sans font-semibold"
              >
                {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating...</> : <><Globe className="w-4 h-4 mr-2" /> Create share link</>}
              </Button>
            </div>
          ) : (
            <>
              {/* Share link */}
              <div>
                <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1.5 block">Share link</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5 text-xs font-mono text-stone-600 truncate" data-testid="collection-share-url">
                    {shareUrl}
                  </div>
                  <Button
                    data-testid="copy-collection-link"
                    onClick={copyLink}
                    variant="outline"
                    className="flex-shrink-0 rounded-xl h-10 px-3 border-stone-200"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-stone-500" />}
                  </Button>
                  <Button
                    data-testid="open-collection-link"
                    onClick={() => window.open(shareUrl, '_blank')}
                    variant="outline"
                    className="flex-shrink-0 rounded-xl h-10 px-3 border-stone-200"
                  >
                    <ExternalLink className="w-4 h-4 text-stone-500" />
                  </Button>
                </div>
                {copied && <p className="font-sans text-xs text-green-600 mt-1">Link copied!</p>}
              </div>

              {/* Social share buttons */}
              <div>
                <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2 block">Share via</label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    data-testid="share-collection-pinterest"
                    onClick={sharePinterest}
                    variant="outline"
                    className="rounded-xl h-11 text-xs font-sans font-semibold border-stone-200 hover:border-[#E60023]/30 hover:bg-[#E60023]/5 transition-all"
                  >
                    <PinterestIcon className="w-4 h-4 mr-2 text-[#E60023]" /> Pinterest
                  </Button>
                  <Button
                    data-testid="share-collection-email"
                    onClick={shareEmail}
                    variant="outline"
                    className="rounded-xl h-11 text-xs font-sans font-semibold border-stone-200 hover:border-stone-400 transition-all"
                  >
                    <Mail className="w-4 h-4 mr-2 text-stone-500" /> Email
                  </Button>
                </div>
              </div>

              {/* Deactivate link */}
              <div className="pt-2 border-t border-stone-100">
                <button
                  data-testid="deactivate-share-link-btn"
                  onClick={deactivateLink}
                  className="flex items-center gap-1.5 font-sans text-xs text-stone-400 hover:text-red-500 transition-colors"
                >
                  <Lock className="w-3.5 h-3.5" /> Disable share link (make private)
                </button>
              </div>
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="share-collection-error">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="font-sans text-xs text-red-700">{error}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CollectionShareDialog;
